import { useToast } from '@chakra-ui/react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import backend from '../services/backend';
import { syncPlatformToken } from '../services/platform';

interface IUserData {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  empresa_id?: string;
  filial_id?: string;
}

interface ISignInCredentials {
  user?: string;
  email?: string;
  password: string;
  empresa?: number | string;
  filial?: number | string;
}

export interface IFilialPermitida {
  id: number;
  nome: string;
}

export interface IEmpresaPermitida {
  id: number;
  nome: string;
  filiais: IFilialPermitida[];
}

export interface ILoginOpcoesData {
  usuario: {
    id: number;
    email: string;
  };
  empresa_padrao?: number;
  filial_padrao?: number;
  empresas: IEmpresaPermitida[];
}

interface IAuthContextData {
  user: IUserData | null;
  isLoading: boolean;
  signIn(credentials: ISignInCredentials): Promise<{
    message: string;
    ssoBootstrapUrl?: string;
  }>;
  obterOpcoesLogin(credentials: ISignInCredentials): Promise<ILoginOpcoesData>;
  signOut(): void;
  permissoes: string[];
}

interface IBackendLoginResponse {
  access_token?: string;
  keycloak_access_token?: string;
  token?: string;
  accessToken?: string;
  refresh_token?: string;
  refreshToken?: string;
  usuario?: Partial<IUserData>;
  user?: Partial<IUserData>;
  permissoes?: string[];
  message?: string;
  sso_bootstrap_url?: string | null;
}

interface IBackendLoginOpcoesResponse {
  success?: boolean;
  data?: ILoginOpcoesData;
}

const CHAVE_ARMAZENAMENTO_TOKEN = 'token';
const CHAVE_ARMAZENAMENTO_REFRESH_TOKEN = 'refreshToken';
const CHAVE_ARMAZENAMENTO_USUARIO = 'user';
const CHAVE_ARMAZENAMENTO_PERMISSOES = 'permissoes';

const AuthContext = createContext<IAuthContextData>({} as IAuthContextData);

function normalizeUser(user?: Partial<IUserData>): IUserData {
  return {
    id: user?.id || '',
    name: user?.name || '',
    email: user?.email || '',
    avatarUrl: user?.avatarUrl,
    empresa_id: user?.empresa_id,
    filial_id: user?.filial_id,
  };
}

function normalizarOpcoesLogin(
  payload: IBackendLoginOpcoesResponse | ILoginOpcoesData,
): ILoginOpcoesData {
  const dadosBrutos =
    'data' in payload && payload.data
      ? payload.data
      : (payload as ILoginOpcoesData);

  const empresas = (dadosBrutos.empresas || []).map(empresa => ({
    id: Number(empresa.id),
    nome: empresa.nome,
    filiais: (empresa.filiais || []).map(filial => ({
      id: Number(filial.id),
      nome: filial.nome,
    })),
  }));

  return {
    usuario: {
      id: Number(dadosBrutos.usuario?.id || 0),
      email: dadosBrutos.usuario?.email || '',
    },
    empresa_padrao:
      dadosBrutos.empresa_padrao !== undefined
        ? Number(dadosBrutos.empresa_padrao)
        : undefined,
    filial_padrao:
      dadosBrutos.filial_padrao !== undefined
        ? Number(dadosBrutos.filial_padrao)
        : undefined,
    empresas,
  };
}

export const AuthProvider: React.FC = ({ children }) => {
  const toast = useToast();
  const [user, setUser] = useState<IUserData | null>(null);
  const [permissoes, setPermissoes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // apaga o header
    delete backend.defaults.headers.common.Authorization;

    // interceptor, limpa tudo e coloca o token
    const interceptor = backend.interceptors.request.use(config => {
      const token = localStorage.getItem(CHAVE_ARMAZENAMENTO_TOKEN);

      // garante que o header esta limpo para colocar o token novo
      const updatedConfig = { ...config };

      if (token) {
        updatedConfig.headers.Authorization = `Bearer ${token}`;
      } else {
        delete updatedConfig.headers.Authorization;
      }

      return updatedConfig;
    });

    return () => backend.interceptors.request.eject(interceptor);
  }, []);

  useEffect(() => {
    const tokenSalvo = localStorage.getItem(CHAVE_ARMAZENAMENTO_TOKEN);
    syncPlatformToken(tokenSalvo);

    const usuarioSalvo = localStorage.getItem(CHAVE_ARMAZENAMENTO_USUARIO);
    const permissoesSalvas = localStorage.getItem(
      CHAVE_ARMAZENAMENTO_PERMISSOES,
    );

    if (usuarioSalvo) {
      try {
        setUser(JSON.parse(usuarioSalvo) as IUserData);
      } catch {
        setUser(null);
      }
    }

    if (permissoesSalvas) {
      try {
        setPermissoes(JSON.parse(permissoesSalvas) as string[]);
      } catch {
        setPermissoes([]);
      }
    }

    setIsLoading(false);
  }, []);

  const signIn = useCallback(async (credentials: ISignInCredentials) => {
    const username = credentials.user || credentials.email;

    const response = await backend.post<IBackendLoginResponse>('/auth/login', {
      user: username,
      username,
      email: credentials.email || username,
      password: credentials.password,
      senha: credentials.password,
      ...(credentials.empresa !== undefined
        ? { empresa: credentials.empresa }
        : {}),
      ...(credentials.filial !== undefined
        ? { filial: credentials.filial }
        : {}),
    });

    const payload = response.data;
    const accessToken =
      payload.keycloak_access_token ||
      payload.access_token ||
      payload.token ||
      payload.accessToken ||
      null;
    const refreshToken = payload.refresh_token || payload.refreshToken || null;
    const backendUser = payload.usuario || payload.user;
    const normalizedUser = normalizeUser(backendUser);
    const newPermissoes = payload.permissoes || [];

    if (!accessToken) {
      throw new Error('Token de acesso não retornado pelo backend.');
    }

    localStorage.setItem(CHAVE_ARMAZENAMENTO_TOKEN, accessToken);
    syncPlatformToken(accessToken);
    if (refreshToken) {
      localStorage.setItem(CHAVE_ARMAZENAMENTO_REFRESH_TOKEN, refreshToken);
    }
    localStorage.setItem(
      CHAVE_ARMAZENAMENTO_USUARIO,
      JSON.stringify(normalizedUser),
    );
    localStorage.setItem(
      CHAVE_ARMAZENAMENTO_PERMISSOES,
      JSON.stringify(newPermissoes),
    );

    setUser(normalizedUser);
    setPermissoes(newPermissoes);

    return {
      message: payload.message || 'Login realizado com sucesso.',
      ssoBootstrapUrl: payload.sso_bootstrap_url || undefined,
    };
  }, []);

  const obterOpcoesLogin = useCallback(
    async (credentials: ISignInCredentials) => {
      const username = credentials.user || credentials.email;
      const response = await backend.post<
        IBackendLoginOpcoesResponse | ILoginOpcoesData
      >('/auth/login/opcoes', {
        user: username,
        username,
        email: credentials.email || username,
        password: credentials.password,
        senha: credentials.password,
      });

      return normalizarOpcoesLogin(response.data);
    },
    [],
  );

  useEffect(() => {
    const tratarLogoutGlobal = (event: StorageEvent) => {
      if (event.key !== 'okton_global_logout' || !event.newValue) {
        return;
      }

      [
        '@DS850:token',
        'meu:token',
        'token',
        '@DS850:refreshToken',
        'meu:refreshToken',
        'refreshToken',
        '@DS850:user',
        'user',
        '@DS850:permissoes',
        'permissoes',
      ].forEach(key => {
        localStorage.removeItem(key);
      });

      syncPlatformToken(undefined);
      setUser(null);
      setPermissoes([]);

      sessionStorage.setItem('okton_sso_skip_once', '1');

      sessionStorage.removeItem('okton_sso_state');

      window.location.replace('/login');
    };

    window.addEventListener('storage', tratarLogoutGlobal);

    return () => {
      window.removeEventListener('storage', tratarLogoutGlobal);
    };
  }, []);

  const signOut = useCallback(async () => {
    try {
      /*
       * Não limpamos o Integra antes do SSO
       * confirmar o início do logout global.
       */
      const response = await backend.post<{
        success?: boolean;
        logout_url?: string;
        message?: string;
      }>('/auth/sso/logout-intent');

      const logoutUrl =
        typeof response.data?.logout_url === 'string'
          ? response.data.logout_url.trim()
          : '';

      if (!logoutUrl) {
        throw new Error('URL de logout global não retornada.');
      }

      const url = new URL(logoutUrl);

      if (
        url.protocol !== 'https:' ||
        url.hostname !== 'sso.okton.com.br' ||
        url.port !== '' ||
        url.pathname !== '/logout' ||
        !url.searchParams.get('code')
      ) {
        throw new Error('URL de logout global inválida.');
      }

      window.location.assign(logoutUrl);
    } catch {
      toast({
        title: 'Não foi possível encerrar a sessão global.',
        description: 'Tente novamente.',
        status: 'error',
        isClosable: true,
        position: 'bottom-right',
      });
    }
  }, [toast]);

  return (
    <AuthContext.Provider
      value={{ user, isLoading, signIn, obterOpcoesLogin, signOut, permissoes }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): IAuthContextData {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado com um AuthProvider');
  }
  return context;
}
